"use client";

import type { ComponentProps, ComponentType, ReactNode } from "react";
import { lazy, Suspense, useEffect, useState } from "react";

import type { RegisterRoutedDialogBeforeCloseHandler } from "@/components/app/dialogs/dialog-types";
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
  DialogDrawerTrigger,
  DialogDrawerType,
} from "@/components/shared/DialogDrawer";
import { Spinner } from "@/components/shared/spinner";
import { CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { DialogName } from "@/utils/dialog-routes";
import { cn } from "@/utils/lib";

const LAZY_DIALOG_EXIT_PRESENCE_MS = 550;

const create = <T,>(loader: () => Promise<{ default: ComponentType<T> }>) => {
  const component = lazy(loader);

  // eslint-disable-next-line react/display-name
  return (
    props: Omit<T, "dialogName" | "open" | "setOpen"> &
      Omit<LazyDialogProps, "component">,
  ) => <LazyDialog component={component} {...props} />;
};

const DialogSkeleton = () => {
  return (
    <div className="h-fit w-full bg-background">
      <DialogDrawerTitle className="hidden">Loading</DialogDrawerTitle>
      <DialogDrawerDescription className="hidden">
        Loading
      </DialogDrawerDescription>
      <div className="flex min-h-28 items-center justify-center md:hidden">
        <Spinner size={24} className="text-primary" />
      </div>
      <div className="hidden md:block">
        <CardHeader
          className="
            flex flex-row items-center justify-between space-y-0 pb-7
          "
        >
          <div className="space-y-1.5">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-10 w-full" />
            <div
              className="
                grid grid-cols-3 gap-2
                md:grid-cols-6
              "
            >
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-4 w-44" />
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center space-x-3">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton className="h-10 flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center space-x-3">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton className="h-10 flex-1" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </div>
  );
};

const ImageEditorLazyFallback = () => {
  return <div className="fixed inset-0 z-100 bg-background/80" />;
};

export type LazyDialogStateProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export type LazyDialogInternalProps = {
  dialogName?: DialogName;
  registerBeforeClose?: RegisterRoutedDialogBeforeCloseHandler;
};

type LazyDialogProps = {
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  contentProps?: ComponentProps<typeof DialogDrawerContent>;
  rootProps?: ComponentProps<typeof DialogDrawer>;
  open?: boolean;
  confirm?: () => boolean | Promise<boolean | unknown>;
  fallback?: ReactNode;
  useDefaultContentClasses?: boolean;
  [key: string]: unknown;
} & LazyDialogInternalProps;

export const LazyDialog = ({
  children,
  component: Component,
  contentProps,
  rootProps,
  open = false,
  fallback = <DialogSkeleton />,
  useDefaultContentClasses = true,
  ...props
}: LazyDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(open);
  const [contentMounted, setContentMounted] = useState(open);
  const routedOnOpenChange =
    typeof props.onOpenChange === "function" ? props.onOpenChange : undefined;
  const isGhostChrome = contentProps?.chrome === "ghost";
  const defaultContentClassName = isGhostChrome
    ? `
        max-h-full w-full touch-pan-y!
      `
    : `
        md:custom-scrollbar
        max-h-full w-full touch-pan-y!
        md:max-h-[98vh] md:max-w-[640px]
      `;

  useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  useEffect(() => {
    if (internalOpen) {
      setContentMounted(true);
      return;
    }

    const timeout = window.setTimeout(() => {
      setContentMounted(false);
    }, LAZY_DIALOG_EXIT_PRESENCE_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [internalOpen]);

  const setDialogOpen = async (nextOpen: boolean) => {
    if (!routedOnOpenChange) {
      setInternalOpen(nextOpen);
      return;
    }

    if (nextOpen) {
      setInternalOpen(true);
      await routedOnOpenChange(true);
      return;
    }

    const result = await routedOnOpenChange(false);

    if (result === false) {
      setInternalOpen(true);
      return;
    }

    setInternalOpen(false);
  };

  const dialogRootProps =
    rootProps?.onOpenChange || !routedOnOpenChange
      ? rootProps
      : {
          ...rootProps,
          onOpenChange: setDialogOpen,
        };

  return (
    <DialogDrawer
      open={internalOpen}
      onOpenChange={setInternalOpen}
      handleOnly
      repositionInputs={false}
      {...dialogRootProps}
    >
      {children && (
        <DialogDrawerTrigger asChild>{children}</DialogDrawerTrigger>
      )}
      <DialogDrawerContent
        {...contentProps}
        className={cn(
          useDefaultContentClasses && defaultContentClassName,
          contentProps?.className,
        )}
        onInteractOutside={(ev) => {
          if (rootProps?.dismissible === false) {
            ev.preventDefault();
            ev.stopPropagation();
          }
        }}
      >
        {contentMounted && (
          <Suspense fallback={fallback}>
            <Component {...props} open={internalOpen} setOpen={setDialogOpen} />
          </Suspense>
        )}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export const WalletTopUpLazyDialog = create(
  () => import("@/components/app/wallet/wallet-top-up-dialog-content"),
);

export const WalletCashOutLazyDialog = create(
  () => import("@/components/app/wallet/wallet-cashout-dialog-content"),
);

export const WalletSwapLazyDialog = create(
  () => import("@/components/app/wallet/wallet-swap-dialog-content"),
);

export const WalletSendLazyDialog = create(
  () => import("@/components/app/wallet/wallet-send-dialog-content"),
);

const TradeCoinMintDialogContent = lazy(
  () => import("@/components/app/trade/actions/trade-coin-mint-dialog-content"),
);

export const TradeCoinMintLazyDialog = (
  props: Omit<
    ComponentProps<typeof TradeCoinMintDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={TradeCoinMintDialogContent}
      contentProps={{
        className: "md:max-w-[425px]",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

const BuySubscriptionDialogContent = lazy(
  () => import("@/components/app/buy-subscription-dialog-content"),
);

const AccountSocialCardDialogContent = lazy(
  () => import("@/components/shared/account-social-card-dialog-content"),
);

const FeedSettingsComingSoonDialogContent = lazy(
  () =>
    import("@/components/app/feed-settings/feed-settings-coming-soon-dialog-content"),
);

const ImageEditorEntryDialog = lazy(
  () => import("@/components/shared/image-editor-entry-dialog"),
);

const CommentOverlayEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/app/editor-v3/editors/comment-overlay-entry-fallback-dialog-content"),
);

const EditorInsertImageEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/app/editor-v3/editors/editor-insert-image-entry-fallback-dialog-content"),
);

const EditorInsertLinkEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/app/editor-v3/editors/editor-insert-link-entry-fallback-dialog-content"),
);

const GifSearchEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/shared/gif-search/gif-search-entry-fallback-dialog-content"),
);

const TradeTransactionSuccessEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/shared/creator-coin-exchange-modal/trade-transaction-success-entry-fallback-dialog-content"),
);

const WalletCashoutSuccessEntryFallbackDialogContent = lazy(
  () =>
    import("@/components/app/wallet/wallet-cashout-success-entry-fallback-dialog-content"),
);

const MessageMembersDialogContent = lazy(
  () => import("@/components/app/messages/message-members-dialog-content"),
);

const DraftManagerDialogContent = lazy(
  () =>
    import("@/components/app/editor-v3/actions/draft-manager-dialog-content"),
);

const GatedContentOptionsDialogContent = lazy(
  () =>
    import("@/components/app/editor-v3/actions/gated-content-options-dialog-content"),
);

const MessageTipDialogContent = lazy(
  () => import("@/components/app/editor-v3/editors/message-tip-dialog-content"),
);

const PayToUnlockMessageDialogContent = lazy(
  () =>
    import("@/components/app/messages/pay-to-unlock-message-dialog-content"),
);

export const BuySubscriptionLazyDialog = (
  props: Omit<
    ComponentProps<typeof BuySubscriptionDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={BuySubscriptionDialogContent}
      fallback={
        <>
          <DialogDrawerTitle className="sr-only">
            Subscription options
          </DialogDrawerTitle>
          <DialogDrawerDescription className="sr-only">
            Loading subscription options.
          </DialogDrawerDescription>
        </>
      }
      contentProps={{
        chrome: "ghost",
        closeClassName: "hidden",
        className: "p-0",
      }}
      {...props}
    />
  );
};

export const AccountSocialCardLazyDialog = (
  props: Omit<
    ComponentProps<typeof AccountSocialCardDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={AccountSocialCardDialogContent}
      contentProps={{
        className: "md:max-w-[420px]",
      }}
      {...props}
    />
  );
};

export const FeedSettingsComingSoonLazyDialog = (
  props: Omit<
    ComponentProps<typeof FeedSettingsComingSoonDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={FeedSettingsComingSoonDialogContent}
      contentProps={{
        className: "md:max-w-[520px]",
      }}
      {...props}
    />
  );
};

export const ImageEditorLazyDialog = (
  props: Omit<ComponentProps<typeof ImageEditorEntryDialog>, "dialogName">,
) => {
  const [hasOpened, setHasOpened] = useState(props.open);

  useEffect(() => {
    if (props.open) {
      setHasOpened(true);
    }
  }, [props.open]);

  if (!hasOpened) {
    return null;
  }

  return (
    <Suspense fallback={<ImageEditorLazyFallback />}>
      <ImageEditorEntryDialog {...props} />
    </Suspense>
  );
};

type LocalEntryLazyDialogProps = LazyDialogInternalProps & {
  instanceId?: string;
  onOpenChange?: unknown;
  open?: boolean;
};

export const PostOverlayEditorLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  void props;

  return null;
};

export const CommentOverlayEditorLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={CommentOverlayEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[520px]",
      }}
      {...props}
    />
  );
};

export const EditorInsertImageLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={EditorInsertImageEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[425px]",
      }}
      {...props}
    />
  );
};

export const EditorInsertLinkLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={EditorInsertLinkEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[425px]",
      }}
      {...props}
    />
  );
};

export const GifSearchLazyDialog = (props: LocalEntryLazyDialogProps) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={GifSearchEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[640px]",
      }}
      {...props}
    />
  );
};

export const TradeTransactionSuccessLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={TradeTransactionSuccessEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[480px]",
      }}
      {...props}
    />
  );
};

export const WalletCashoutSuccessLazyDialog = (
  props: LocalEntryLazyDialogProps,
) => {
  if (props.instanceId) {
    return null;
  }

  return (
    <LazyDialog
      component={WalletCashoutSuccessEntryFallbackDialogContent}
      contentProps={{
        className: "md:max-w-[400px]",
      }}
      {...props}
    />
  );
};

export const MessageMembersLazyDialog = (
  props: Omit<
    ComponentProps<typeof MessageMembersDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={MessageMembersDialogContent}
      contentProps={{
        className: "custom-scrollbar md:max-w-[520px]",
      }}
      {...props}
    />
  );
};

const SubscribersDialogContent = lazy(
  () => import("@/components/app/profile/subscribers-dialog-content"),
);

export const SubscribersLazyDialog = (
  props: Omit<
    ComponentProps<typeof SubscribersDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={SubscribersDialogContent}
      contentProps={{
        className: "md:max-h-[80vh] md:max-w-[600px]",
      }}
      {...props}
    />
  );
};

export const DraftManagerLazyDialog = (
  props: Omit<
    ComponentProps<typeof DraftManagerDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={DraftManagerDialogContent}
      contentProps={{
        className: "overflow-y-auto md:max-w-[560px]",
      }}
      {...props}
    />
  );
};

export const GatedContentOptionsLazyDialog = (
  props: Omit<
    ComponentProps<typeof GatedContentOptionsDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={GatedContentOptionsDialogContent}
      contentProps={{
        className: "w-full overflow-y-auto",
      }}
      {...props}
    />
  );
};

export const MessageTipLazyDialog = (
  props: Omit<
    ComponentProps<typeof MessageTipDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={MessageTipDialogContent}
      contentProps={{
        className: "md:max-w-[420px]",
      }}
      {...props}
    />
  );
};

export const PayToUnlockMessageLazyDialog = (
  props: Omit<
    ComponentProps<typeof PayToUnlockMessageDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={PayToUnlockMessageDialogContent}
      contentProps={{
        className: "md:max-w-[440px]",
      }}
      {...props}
    />
  );
};

const BountyRequestDialogContent = lazy(
  () => import("@/components/bounty/bounty-request-dialog-content"),
);

const AddPostDialogContent = lazy(
  () => import("@/components/app/app-layout/add-post-dialog-content"),
);

const EditPostDialogContent = lazy(
  () => import("@/components/app/post/post-actions/edit-post-dialog-content"),
);

const ManageTierDialogContent = lazy(
  () =>
    import("@/components/app/settings/subscriptions/manage-tier-dialog-content"),
);

export const AddPostLazyDialog = (
  props: Omit<
    ComponentProps<typeof AddPostDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={AddPostDialogContent}
      contentProps={{
        bodyClassName: "unforce-radius rounded-none border-0 shadow-none",
        className: `
          content unforce-radius h-(--overlay-keyboard-available-height)
          max-h-(--overlay-keyboard-available-height) max-w-full overflow-hidden rounded-none border-0
          md:max-h-[90vh] md:max-w-[700px] md:rounded-lg
        `,
        closeClassName: "top-2 right-2 size-9 p-2",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      useDefaultContentClasses={false}
      {...props}
    />
  );
};

export const EditPostLazyDialog = (
  props: Omit<
    ComponentProps<typeof EditPostDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={EditPostDialogContent}
      contentProps={{
        bodyClassName: "unforce-radius rounded-none border-0 shadow-none",
        className: `
          content unforce-radius h-(--overlay-keyboard-available-height)
          max-h-(--overlay-keyboard-available-height) max-w-full overflow-hidden rounded-none border-0
          md:max-h-[90vh] md:max-w-[700px] md:rounded-lg
        `,
        closeClassName: "top-2 right-2 size-9 p-2",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      useDefaultContentClasses={false}
      {...props}
    />
  );
};

export const ManageTierLazyDialog = (
  props: Omit<
    ComponentProps<typeof ManageTierDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={ManageTierDialogContent}
      contentProps={{
        bodyClassName: "flex! min-h-0 flex-col overflow-hidden!",
        className: "max-w-3xl overflow-hidden p-0",
        closeClassName: "top-3 right-3",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

export const BountyRequestLazyDialog = (
  props: Omit<
    ComponentProps<typeof BountyRequestDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={BountyRequestDialogContent}
      contentProps={{
        className: "md:max-w-2xl",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

export const ClaimBountyLazyDialog = create(
  () => import("@/components/bounty/claim-bounty-dialog-content"),
);

export const PageShareLazyDialog = create(
  () => import("@/components/shared/page-share-dialog-content"),
);

export const SearchLazyDialog = create(
  () => import("@/components/app/app-layout/search-dialog-content"),
);

const FeedbackDialogContent = lazy(
  () =>
    import("@/components/app/app-layout/sidebar-top-resutls-widget/feedback-dialog-content"),
);

export const FeedbackLazyDialog = (
  props: Omit<
    ComponentProps<typeof FeedbackDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={FeedbackDialogContent}
      contentProps={{
        className: "md:max-w-lg",
      }}
      {...props}
    />
  );
};

const NewMessageDialogContent = lazy(
  () => import("@/components/app/messages/messages-new-message-dialog-content"),
);

export const NewMessageLazyDialog = (
  props: Omit<
    ComponentProps<typeof NewMessageDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={NewMessageDialogContent}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

export const CustomMessageSettingsLazyDialog = create(
  () => import("@/components/app/messages/custom-settings-dialog-content"),
);

const CreateReferralCodeDialogContent = lazy(
  () =>
    import("@/components/app/settings/referral-code-settings/create-referral-code-dialog-content"),
);

export const CreateReferralCodeLazyDialog = (
  props: Omit<
    ComponentProps<typeof CreateReferralCodeDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={CreateReferralCodeDialogContent}
      contentProps={{
        className: "md:max-w-[425px]",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

const GetTokensEarlyDialogContent = lazy(
  () => import("@/components/shared/get-tokens-early-dialog-content"),
);

const CashoutAmmDialogContent = lazy(
  () => import("@/components/app/trade/new/cashout-amm-dialog-content"),
);

const ImageViewerDialogContent = lazy(
  () => import("@/components/shared/image-viewer-dialog-content"),
);

const TradeCoinDialogContent = lazy(
  () =>
    import("@/components/shared/creator-coin-exchange-modal/trade-coin-dialog-content"),
);

export const CashoutAmmLazyDialog = (
  props: Omit<
    ComponentProps<typeof CashoutAmmDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={CashoutAmmDialogContent}
      contentProps={{
        className: "sm:max-w-[425px]",
      }}
      {...props}
    />
  );
};

export const ImageViewerLazyDialog = (
  props: Omit<
    ComponentProps<typeof ImageViewerDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps"> & {
      instanceId?: string;
    },
) => {
  if (props.instanceId) {
    return null;
  }

  const { fallback, ...dialogProps } = props;
  const fallbackNode = fallback as ReactNode | undefined;
  const isPostGallery = Boolean(props.postHash);

  return (
    <LazyDialog
      component={ImageViewerDialogContent}
      fallback={
        fallbackNode !== undefined ? (
          fallbackNode
        ) : (
          <div
            className={cn(
              "flex items-center justify-center",
              isPostGallery ? "size-full" : "min-h-40 min-w-40 p-8",
            )}
          >
            <DialogDrawerTitle className="sr-only">
              Loading image viewer
            </DialogDrawerTitle>
            <DialogDrawerDescription className="sr-only">
              Loading image viewer.
            </DialogDrawerDescription>
            <Spinner size={32} className="text-primary" />
          </div>
        )
      }
      contentProps={{
        chrome: isPostGallery ? "ghost" : "default",
        className: isPostGallery
          ? `
              h-screen max-h-screen w-screen max-w-[100vw] rounded-none
              border-0 bg-black/20 p-0
            `
          : "max-h-max w-full max-w-max",
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      useDefaultContentClasses={!isPostGallery}
      {...dialogProps}
    />
  );
};

export const GetTokensEarlyLazyDialog = (
  props: Omit<
    ComponentProps<typeof GetTokensEarlyDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={GetTokensEarlyDialogContent}
      contentProps={{
        className: "md:max-w-[520px]",
      }}
      {...props}
    />
  );
};

export const TradeCoinLazyDialog = (
  props: Omit<
    ComponentProps<typeof TradeCoinDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={TradeCoinDialogContent}
      contentProps={{
        className: `
          flex max-w-full flex-col overflow-auto
          md:max-w-(--breakpoint-sm)
        `,
      }}
      {...props}
    />
  );
};

const HoldersDialogContent = lazy(
  () => import("@/components/app/trade/actions/holders-dialog-content"),
);

export const HoldersLazyDialog = (
  props: Omit<
    ComponentProps<typeof HoldersDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={HoldersDialogContent}
      contentProps={{
        className: `
          custom-scrollbar md:max-h-[90vh] md:max-w-(--breakpoint-md)
        `,
      }}
      {...props}
    />
  );
};

const FollowDialogContent = lazy(
  () => import("@/components/shared/follow-modal/follow-dialog-content"),
);

export const FollowLazyDialog = (
  props: Omit<
    ComponentProps<typeof FollowDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={FollowDialogContent}
      contentProps={{
        className: `
          md:min-h-[550px] md:max-h-[80vh] md:max-w-[600px]
        `,
        closeClassName: "top-[48px] right-4",
      }}
      {...props}
    />
  );
};

export const FollowUserListsLazyDialog = create(
  () => import("@/components/app/lists/follow-user-lists-dialog-content"),
);

const PollVotersDialogContent = lazy(
  () =>
    import("@/components/app/post/poll-voters-modal/poll-voters-dialog-content"),
);

export const PollVotersLazyDialog = (
  props: Omit<
    ComponentProps<typeof PollVotersDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={PollVotersDialogContent}
      contentProps={{
        className: `
          md:max-h-[80vh] md:max-w-(--breakpoint-sm)
        `,
      }}
      {...props}
    />
  );
};

export const PostActivityLazyDialog = create(
  () =>
    import("@/components/app/post/post-activity-modal/post-activity-modal-content"),
);

const UnlockTokensDialogContent = lazy(
  () => import("@/components/app/wallet/unlock-tokens-dialog-content"),
);

export const UnlockTokensLazyDialog = (
  props: Omit<
    ComponentProps<typeof UnlockTokensDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={UnlockTokensDialogContent}
      contentProps={{
        className: `
          md:max-h-[80vh] md:max-w-(--breakpoint-sm)
        `,
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

export const ReportPostLazyDialog = create(
  () => import("@/components/app/post/post-actions/report-post-modal"),
);

export const ReportUserLazyDialog = create(
  () => import("@/components/app/profile/profile-report-modal"),
);

const UpdateEmailDialogContent = lazy(
  () => import("@/components/shared/update-email-dialog-content"),
);

export const UpdateEmailLazyDialog = (
  props: Omit<
    ComponentProps<typeof UpdateEmailDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={UpdateEmailDialogContent}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

const UpdateProfileDialogContent = lazy(
  () => import("@/components/app/profile/update-profile-dialog-content"),
);

export const UpdateProfileLazyDialog = (
  props: Omit<
    ComponentProps<typeof UpdateProfileDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={UpdateProfileDialogContent}
      contentProps={{
        bodyClassName: `
          unforce-radius rounded-none border-0 shadow-none
          md:rounded-2xl md:border md:shadow-lg
        `,
        className: `
          inset-x-0! top-0! h-(--overlay-keyboard-available-height)!
          max-h-(--overlay-keyboard-available-height)! w-full! max-w-full!
          translate-0! overflow-auto p-0
          md:top-(--dialog-content-center-y)! md:right-auto!
          md:left-[50%]! md:h-auto! md:max-h-[90vh] md:max-w-[860px]!
          md:-translate-1/2!
        `,
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      {...props}
    />
  );
};

const QuotePostDialogContent = lazy(
  () => import("@/components/app/post/post-activity/quote-post-dialog-content"),
);

export const QuotePostLazyDialog = (
  props: Omit<
    ComponentProps<typeof QuotePostDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps">,
) => {
  return (
    <LazyDialog
      component={QuotePostDialogContent}
      contentProps={{
        bodyClassName: "overflow-visible",
        className: `
          max-h-full w-full touch-pan-y! overflow-visible!
          md:max-h-[90vh] md:max-w-[640px]
        `,
      }}
      rootProps={{
        type: DialogDrawerType.Dialog,
      }}
      useDefaultContentClasses={false}
      {...props}
    />
  );
};

export const CreateCollectionLazyDialog = create(
  () =>
    import("@/app/(app)/collections/[userPublicKey]/create-collection-dialog"),
);

export const UserWeightLazyModal = create(
  () =>
    import("@/components/app/admin/feed-configuration/add-user-weight-dialog"),
);

export const PostWeightLazyModal = create(
  () =>
    import("@/components/app/admin/feed-configuration/add-post-weight-dialog"),
);

export const FeedManagerLazyDialog = create(
  () => import("@/components/app/feed/feed-manager/feed-manager-dialog-host"),
);

export const NotificationViewManagerLazyDialog = create(
  () =>
    import("@/components/app/notifications/notification-view-manager-dialog-host"),
);

const ListEditorLazyDialog = create(
  () => import("@/components/app/feed/feed-manager/list-editor-dialog-content"),
);

export const CreateListLazyDialog = ListEditorLazyDialog;
export const EditListLazyDialog = ListEditorLazyDialog;

const ViewListDialogContent = lazy(
  () => import("@/components/app/lists/view-list-dialog-content"),
);

export const ViewListLazyDialog = (
  props: Omit<
    ComponentProps<typeof ViewListDialogContent>,
    "dialogName" | "open" | "setOpen"
  > &
    Omit<LazyDialogProps, "component" | "contentProps" | "rootProps">,
) => {
  return (
    <LazyDialog
      component={ViewListDialogContent}
      contentProps={{
        className: `
          custom-scrollbar flex max-h-(--overlay-content-max-height)
          max-w-full flex-col gap-0 overflow-auto p-0
          md:min-h-[550px] md:max-h-[80vh]
          md:max-w-(--breakpoint-sm)
        `,
        closeClassName: "top-[21px] right-4",
      }}
      useDefaultContentClasses={false}
      {...props}
    />
  );
};

const CustomFeedEditorLazyDialog = create(
  () =>
    import("@/components/app/feed/feed-manager/custom-feed-editor-dialog-content"),
);

export const CreateCustomFeedLazyDialog = CustomFeedEditorLazyDialog;
export const EditCustomFeedLazyDialog = CustomFeedEditorLazyDialog;
